import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Check, Copy, Crown, Lock, LogOut, UserX, WifiOff } from 'lucide-react';
import { MIN_PLAYERS, validateSettings, type LobbyView } from '@mafia/core';
import { emit } from '@/lib/socket';
import { useAuth } from '@/store/auth';
import { useGame } from '@/store/game';
import { Avatar } from '@/components/Avatar';
import { KindBadge } from '@/components/Kind';
import { Badge, Button, Card } from '@/components/ui';
import { SettingsEditor, SettingsSummary, playerCount, translateIssue } from './SettingsEditor';

export function Lobby({ lobby }: { lobby: LobbyView }) {
  const me = useAuth((s) => s.user)!;
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);
  const isHost = lobby.hostId === me.id;
  const myMember = lobby.members.find((m) => m.userId === me.id);

  const count = playerCount(lobby);
  const notReady = lobby.members.filter((m) => !m.ready && !m.isHost);
  const offline = lobby.members.filter((m) => !m.connected);
  const issues = validateSettings(lobby.settings, count).filter((i) => !i.startsWith('players must'));
  const blocker =
    count < MIN_PLAYERS
      ? `Нужно ещё ${MIN_PLAYERS - count} — минимум ${MIN_PLAYERS} игроков`
      : offline.length
        ? `Не в сети: ${offline.map((m) => m.nickname).join(', ')}`
        : notReady.length
          ? `Ждём готовности: ${notReady.map((m) => m.nickname).join(', ')}`
          : issues.length
            ? translateIssue(issues[0]!)
            : null;

  async function copyCode() {
    await navigator.clipboard?.writeText(lobby.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function leave() {
    await emit('room:leave');
    useGame.setState({ lobby: null });
    navigate('/');
  }

  async function start() {
    setStarting(true);
    await emit('game:start');
    setStarting(false);
  }

  return (
    <div className="mx-auto grid max-w-6xl gap-4 px-4 py-6 lg:grid-cols-[1fr_400px]">
      <Card className="p-5">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-display text-2xl">
              {lobby.isPrivate && <Lock className="h-5 w-5 text-gold-400" />}
              {lobby.name}
              <KindBadge kind={lobby.kind} showAmateur />
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              {count} из {lobby.maxPlayers} игроков{lobby.moderated ? ' + ведущий' : ''} · пригласите друзей по коду
            </p>
          </div>
          <button
            onClick={copyCode}
            className="flex items-center gap-2 rounded-xl border border-white/10 bg-ink-850 px-3 py-2 font-mono text-lg tracking-[.25em] hover:border-gold-400/40"
            title="Скопировать код"
          >
            {lobby.code}
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-zinc-500" />}
          </button>
        </div>

        <ul className="grid gap-2 sm:grid-cols-2">
          {lobby.members.map((m) => (
            <li
              key={m.userId}
              className={clsx(
                'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition',
                m.ready || m.isHost ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-white/5 bg-ink-850',
                !m.connected && 'opacity-50',
              )}
            >
              <Avatar id={m.avatarId} name={m.nickname} size={36} />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                  {m.nickname}
                  {m.userId === me.id && <span className="text-xs text-zinc-500">(вы)</span>}
                </p>
                <p className="flex items-center gap-1 text-xs text-zinc-500">
                  {m.isHost ? (
                    <>
                      <Crown className="h-3 w-3 text-gold-400" /> {lobby.moderated ? 'Ведущий, без роли' : 'Хост'}
                    </>
                  ) : m.ready ? (
                    <span className="text-emerald-400">Готов</span>
                  ) : (
                    'Не готов'
                  )}
                  {!m.connected && (
                    <>
                      {' · '}
                      <WifiOff className="h-3 w-3" /> не в сети
                    </>
                  )}
                </p>
              </div>
              {isHost && !m.isHost && (
                <button
                  onClick={() => emit('room:kick', { userId: m.userId })}
                  className="rounded-lg p-1.5 text-zinc-600 hover:bg-blood-600/10 hover:text-blood-400"
                  title="Исключить"
                  aria-label={`Исключить ${m.nickname}`}
                >
                  <UserX className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
          {Array.from({ length: Math.max(0, lobby.maxPlayers - count) }).map((_, i) => (
            <li key={`empty-${i}`} className="flex items-center gap-3 rounded-xl border border-dashed border-white/5 px-3 py-2.5 text-sm text-zinc-600">
              <span className="h-9 w-9 rounded-full border border-dashed border-white/10" /> Свободное место
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-col gap-3 border-t border-white/5 pt-5 sm:flex-row sm:items-center">
          {isHost ? (
            <div className="flex-1">
              <Button size="lg" className="w-full sm:w-auto" disabled={!!blocker} loading={starting} onClick={start}>
                Начать игру
              </Button>
              {blocker && <p className="mt-2 text-sm text-zinc-500">{blocker}</p>}
            </div>
          ) : (
            <div className="flex-1">
              <Button
                size="lg"
                variant={myMember?.ready ? 'secondary' : 'gold'}
                className="w-full sm:w-auto"
                onClick={() => emit('room:ready', { ready: !myMember?.ready })}
              >
                {myMember?.ready ? 'Я не готов' : 'Я готов'}
              </Button>
              <p className="mt-2 text-sm text-zinc-500">Игру запускает хост, когда все готовы.</p>
            </div>
          )}
          <Button variant="ghost" onClick={leave}>
            <LogOut className="h-4 w-4" /> Выйти из комнаты
          </Button>
        </div>
      </Card>

      <Card className="h-fit p-5 lg:sticky lg:top-20">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg">Настройки игры</h2>
          {!isHost && <Badge>меняет хост</Badge>}
        </div>
        {isHost ? <SettingsEditor lobby={lobby} canModerate={me.role === 'ADMIN'} /> : <SettingsSummary lobby={lobby} />}
      </Card>
    </div>
  );
}
